import { useState } from 'react';
import {
  Ticket, ChevronDown, ChevronRight, AlertTriangle, Lightbulb,
  Tag, User, Calendar, Database, Layers, Search,
} from 'lucide-react';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { FeedbackButtons } from '../feedback/FeedbackButtons';
import type { Message as MessageType, JiraHypothesis } from '../../types';

interface JiraTicketPanelProps {
  message: MessageType;
}

/** Colour badge for severity. */
function SeverityBadge({ severity }: { severity?: string }) {
  if (!severity) return null;
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200',
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${colors[severity] || colors.medium}`}
    >
      {severity}
    </span>
  );
}

/** Single hypothesis card with expand/collapse. */
function HypothesisCard({ h, index }: { h: JiraHypothesis; index: number }) {
  const [expanded, setExpanded] = useState(index === 0); // first expanded by default
  const pct = Math.round(h.confidence * 100);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
        <Lightbulb size={14} className="text-amber-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-800 flex-1">
          Hypothesis {index + 1}: {h.title}
        </span>
        <span
          className="text-[10px] font-mono px-1.5 py-0.5 rounded"
          style={{
            background: pct >= 70 ? '#dcfce7' : pct >= 50 ? '#fef9c3' : '#f3f4f6',
            color: pct >= 70 ? '#15803d' : pct >= 50 ? '#a16207' : '#6b7280',
          }}
        >
          {pct}%
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
          <p className="text-xs text-gray-600 leading-relaxed">{h.description}</p>

          {h.evidence.length > 0 && (
            <div>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Evidence</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {h.evidence.map((e, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                    {e}
                  </span>
                ))}
              </div>
            </div>
          )}

          {h.suggestedActions.length > 0 && (
            <div>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Suggested Actions</span>
              <ul className="mt-1 space-y-0.5">
                {h.suggestedActions.map((a, i) => (
                  <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                    <span className="text-primary-500 mt-0.5">•</span>
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renders a JIRA ticket analysis response with:
 * - Structured ticket details
 * - Module & table identification
 * - Multiple ranked hypotheses with evidence and suggested actions
 */
export function JiraTicketPanel({ message }: JiraTicketPanelProps) {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const jira = message.jiraMeta;

  if (!jira) return null;

  const analysis = jira.analysis;

  return (
    <div className="flex gap-2.5 animate-fade-in">
      {/* Avatar */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-orange-100">
        <Ticket size={13} className="text-orange-600" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-gray-900">{jira.ticketKey}</span>
          <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">
            {jira.issueType}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded font-medium">
            {jira.status}
          </span>
          {analysis && <SeverityBadge severity={analysis.severity} />}
          {analysis?.category && (
            <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded font-medium">
              {analysis.category}
            </span>
          )}
        </div>

        {/* Summary */}
        <div className="text-sm text-gray-800 font-medium leading-snug">
          {jira.summary}
        </div>

        {/* Ticket details (collapsible) */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setDetailsExpanded(!detailsExpanded)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
          >
            {detailsExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            <span className="text-xs font-semibold text-gray-600">Ticket Details</span>
          </button>
          {detailsExpanded && (
            <div className="px-3 pb-3 border-t border-gray-100 pt-2 space-y-1.5">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div className="flex items-center gap-1 text-gray-500">
                  <Layers size={10} /> Project:
                  <span className="text-gray-800 font-medium">{jira.project}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <AlertTriangle size={10} /> Priority:
                  <span className="text-gray-800 font-medium">{jira.priority || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <User size={10} /> Assignee:
                  <span className="text-gray-800 font-medium">{jira.assignee || 'Unassigned'}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <User size={10} /> Reporter:
                  <span className="text-gray-800 font-medium">{jira.reporter || 'N/A'}</span>
                </div>
                {jira.created && (
                  <div className="flex items-center gap-1 text-gray-500">
                    <Calendar size={10} /> Created:
                    <span className="text-gray-800 font-medium">
                      {new Date(jira.created).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {jira.updated && (
                  <div className="flex items-center gap-1 text-gray-500">
                    <Calendar size={10} /> Updated:
                    <span className="text-gray-800 font-medium">
                      {new Date(jira.updated).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {jira.components.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] text-gray-500">Components:</span>
                  {jira.components.map((c) => (
                    <span key={c} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded">{c}</span>
                  ))}
                </div>
              )}
              {jira.labels.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <Tag size={10} className="text-gray-400" />
                  {jira.labels.map((l) => (
                    <span key={l} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{l}</span>
                  ))}
                </div>
              )}

              {jira.description && (
                <div className="mt-2">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Description</span>
                  <div className="mt-1 text-xs text-gray-600 leading-relaxed bg-gray-50 rounded p-2 max-h-40 overflow-y-auto">
                    <MarkdownRenderer content={jira.description} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Module & Schema context */}
        {analysis && (analysis.moduleName || analysis.relatedTables.length > 0) && (
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Database size={12} className="text-indigo-500" />
              <span className="text-xs font-semibold text-indigo-700">Schema Context</span>
            </div>
            {analysis.moduleName && (
              <div className="text-xs text-indigo-600">
                <span className="font-medium">Module:</span> {analysis.moduleName}
              </div>
            )}
            {analysis.relatedTables.length > 0 && (
              <div className="flex flex-wrap gap-1">
                <span className="text-[10px] text-indigo-500 font-medium">Tables:</span>
                {analysis.relatedTables.map((t) => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 bg-white border border-indigo-200 text-indigo-700 rounded font-mono">
                    {t}
                  </span>
                ))}
              </div>
            )}
            {analysis.relatedFields.length > 0 && (
              <div className="flex flex-wrap gap-1">
                <span className="text-[10px] text-indigo-500 font-medium">Fields:</span>
                {analysis.relatedFields.map((f) => (
                  <span key={f} className="text-[10px] px-1.5 py-0.5 bg-white border border-indigo-200 text-indigo-600 rounded font-mono">
                    {f}
                  </span>
                ))}
              </div>
            )}
            {analysis.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                <Search size={10} className="text-indigo-400 mt-0.5" />
                {analysis.keywords.map((k) => (
                  <span key={k} className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded">
                    {k}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hypotheses section */}
        {analysis && analysis.hypotheses.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Lightbulb size={12} className="text-amber-500" />
              <span className="text-xs font-semibold text-gray-700">
                Root Cause Hypotheses ({analysis.hypotheses.length})
              </span>
            </div>
            {analysis.hypotheses.map((h, i) => (
              <HypothesisCard key={i} h={h} index={i} />
            ))}
          </div>
        )}

        {/* Feedback */}
        <div className="flex items-center gap-2 pt-1">
          <FeedbackButtons messageId={message.id} currentFeedback={message.feedbackGiven ?? null} />
        </div>
      </div>
    </div>
  );
}
